import moment from 'moment';
import { sha256 } from 'js-sha256';

/**
 * GTM funnel transformer for ELAL requirements.
 * Emits a unified custom event with event_name and ecommerce payload.
 */

const FUNNEL_EVENT = 'arbitrip_funnel';

const DEFAULT_CURRENCY = 'USD';

const MAX_LIST_ITEMS = 20;
const SINGLE_CURRENCY_COUNT = 1;

// Top-level funnel fields safe to send to any brand's container (see toBrandNeutralFunnelEvent).
// Deliberately an allowlist: everything else is dropped, so a brand-specific field added later —
// e.g. El Al loyalty/member identity (email, member_id, matmid_id, customer_tier, points_spent,
// points_used, transaction_cash_value_of_points) — is never shared by default.
const BRAND_NEUTRAL_FUNNEL_FIELDS = new Set([
    'event',
    'event_name',
    'ecommerce',
    'check_in',
    'check_out',
    'search_trip_length',
    'search_days_to_trip',
    'address',
    'rooms',
    'guests',
    'adults',
    'children',
    'city',
    'country',
    'country_code',
    'results_count',
    'selected_rank',
    'error_code',
    'coupon_description',
    'payment_type',
    'total_value',
    'free_cancellation_until',
    'order_date'
]);

const EMAIL_DOMAIN_GMAIL = 'gmail.com';
const EMAIL_DOMAIN_GOOGLEMAIL = 'googlemail.com';

const EMAIL_VALUE_ANONYMOUS = 'NA';

const DATE_INPUT_FORMATS = [
    moment.ISO_8601,
    'YYYY-MM-DD',
    'DD MMM YYYY',
    'D MMM YYYY',
    'DD/MM/YYYY',
    'D/M/YYYY'
];

function toNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function roundMoney(value) {
    const num = toNumber(value);
    if (num === null) {
        return null;
    }
    // Round to 2 decimals to avoid floating-point noise in GA4 monetary fields.
    return Math.round((num + Number.EPSILON) * 100) / 100;
}

function removeEmpty(obj) {
    return Object.fromEntries(
        Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined && value !== '')
    );
}

function pickString(...values) {
    for (const value of values) {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return String(value);
        }
    }
    return null;
}

function normalizeEmailForHashing(email) {
    const normalized = email.trim().toLowerCase();
    const atIndex = normalized.lastIndexOf('@');
    if (atIndex === -1) {
        return normalized;
    }
    let local = normalized.slice(0, atIndex);
    const domain = normalized.slice(atIndex + 1);
    if (domain === EMAIL_DOMAIN_GMAIL || domain === EMAIL_DOMAIN_GOOGLEMAIL) {
        local = local.replace(/\./g, '');
    }
    return `${local}@${domain}`;
}

function hashEmail(email) {
    const normalizedEmail = pickString(email);
    if (!normalizedEmail) {
        return null;
    }
    return sha256(normalizeEmailForHashing(normalizedEmail));
}

function normalizeDate(date) {
    if (!date) return null;

    if (moment.isMoment(date)) {
        return date.isValid() ? date.clone() : null;
    }

    if (date instanceof Date) {
        const parsedDate = moment(date);
        return parsedDate.isValid() ? parsedDate : null;
    }

    // Try strict parsing in current locale first, then English month names.
    const parsed = moment(date, DATE_INPUT_FORMATS, true);
    if (parsed.isValid()) return parsed;

    const parsedEn = moment(date, DATE_INPUT_FORMATS, 'en', true);
    if (parsedEn.isValid()) return parsedEn;

    // Only allow strict ISO as a final fallback to avoid moment's JS Date fallback warning.
    const iso = moment(date, moment.ISO_8601, true);
    return iso.isValid() ? iso : null;
}

function formatDate(date) {
    const normalized = normalizeDate(date);
    return normalized ? normalized.format('DD/MM/YYYY') : null;
}

function getTripLength(checkIn, checkOut) {
    const checkInDate = normalizeDate(checkIn);
    const checkOutDate = normalizeDate(checkOut);
    if (!checkInDate || !checkOutDate) return null;
    return checkOutDate.diff(checkInDate, 'days');
}

function getDaysToTrip(checkIn) {
    const checkInDate = normalizeDate(checkIn);
    if (!checkInDate) return null;
    return checkInDate.startOf('day').diff(moment().startOf('day'), 'days');
}

function getRoomsCount(properties) {
    const rooms = toNumber(properties.rooms);
    return rooms !== null && rooms > 0 ? rooms : 1;
}

function getChildrenPerRoom(properties) {
    if (Array.isArray(properties.children_ages)) return properties.children_ages.length;
    const childrenCount = toNumber(properties.children_count ?? properties.children);
    return childrenCount ?? null;
}

function getChildrenCount(properties) {
    const childrenPerRoom = getChildrenPerRoom(properties);
    if (childrenPerRoom === null) return null;
    return childrenPerRoom * getRoomsCount(properties);
}

function getAdultsCount(properties) {
    const adultsPerRoom = toNumber(properties.guests);
    if (adultsPerRoom === null) return null;
    return adultsPerRoom * getRoomsCount(properties);
}

function getGuestsCount(properties) {
    const adults = getAdultsCount(properties);
    const children = getChildrenCount(properties);
    if (adults === null && children === null) return null;
    return (adults ?? 0) + (children ?? 0);
}

function hashMatmidId(value) {
    const normalized = pickString(value);
    return normalized ? sha256(String(normalized).trim()) : null;
}

function getAddress(properties) {
    return pickString(
        properties.address,
        properties.name,
        properties.hotel_name,
        [properties.city, properties.country].filter(Boolean).join(', ')
    );
}

function getEventTotalPrice(properties) {
    return toNumber(
        properties.total_price ??
        properties.totalPrice ??
        properties.total_value ??
        properties.value
    );
}

function getValueCurrency(properties) {
    return pickString(properties.currency, properties.charge_currency, properties.display_currency) || DEFAULT_CURRENCY;
}

function resolvePreferredCurrency(amount, properties) {
    // El Al GA4 converts to its reporting currency on ingestion. We send the deal's
    // native currency and value as-is so value/price are never dropped on conversion failure.
    return { currency: getValueCurrency(properties), amount: roundMoney(amount) };
}

function normalizeErrorCode(error, fallback) {
    const candidate = pickString(
        error?.error_code,
        error?.code,
        error?.status,
        error?.type,
        error?.name,
        error?.message,
        fallback
    );
    if (!candidate) return 'UNKNOWN';

    return String(candidate)
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toUpperCase() || 'UNKNOWN';
}

function getCommonFields(properties) {
    const formattedCheckIn = formatDate(properties.check_in);
    const formattedCheckOut = formatDate(properties.check_out);
    const tripLength = getTripLength(properties.check_in, properties.check_out);
    const daysToTrip = getDaysToTrip(properties.check_in);
    const children = getChildrenCount(properties);
    const adults = getAdultsCount(properties);
    return removeEmpty({
        check_in: formattedCheckIn,
        check_out: formattedCheckOut,
        search_trip_length: tripLength,
        search_days_to_trip: daysToTrip,
        address: getAddress(properties),
        rooms: toNumber(properties.rooms),
        guests: getGuestsCount(properties),
        adults,
        children,
        city: pickString(properties.city),
        country: pickString(properties.country),
        country_code: pickString(properties.country_code, properties.countryCode),
        email: properties.anonymous ? EMAIL_VALUE_ANONYMOUS : hashEmail(properties.email),
        member_id: hashMatmidId(properties.member_id),
        matmid_id: hashMatmidId(properties.matmid_id),
        customer_tier: pickString(properties.customer_tier)
    });
}

function normalizePropertyTypeCase(value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getPropertyType(source = {}, defaults = {}) {
    // Normalize casing so the same property type is consistent across funnel events
    // (suppliers send 'hotel', defaults inject 'Hotel' — collapse to one canonical value).
    const propertyType = pickString(
        source.property_type,
        source.stay_type,
        source.autocomplete_category,
        source.item_varient,
        defaults.property_type,
        defaults.stay_type,
        defaults.autocomplete_category,
        defaults.item_varient
    );
    return propertyType ? normalizePropertyTypeCase(propertyType) : null;
}

function getItemCategory(source = {}, defaults = {}) {
    const propertyType = getPropertyType(source, defaults);
    if (!propertyType) {
        return null;
    }

    const normalizedType = propertyType.trim().toLowerCase();
    if (
        normalizedType.includes('apartment') ||
        normalizedType.includes('appartment') ||
        normalizedType.includes('home') ||
        normalizedType.includes('villa') ||
        normalizedType.includes('condo') ||
        normalizedType.includes('residence')
    ) {
        return 'Apartment';
    }

    return 'Hotel';
}

function getItemSubcategory(source = {}, defaults = {}) {
    return pickString(
        source.city,
        source.hotel?.city,
        source.details?.city,
        defaults.city,
        defaults.hotel?.city,
        defaults.details?.city
    );
}

function getPurchaseVariant(source = {}, defaults = {}, roomName) {
    return pickString(
        source.purchase_variant,
        getPropertyType(source, defaults),
        roomName,
        source.board_code,
        source.rate_plan_name,
        source.rate_name,
        defaults.purchase_variant
    );
}

function normalizeHotelItem(source = {}, index = 1, defaults = {}, options = {}) {
    const { includePurchaseVariant = false } = options;
    const totalPrice = toNumber(
        source.total_price ??
        source.totalPrice ??
        defaults.total_price ??
        defaults.totalPrice
    );
    const nights = toNumber(source.nights ?? defaults.nights);
    const rooms = toNumber(source.quantity ?? source.rooms ?? defaults.quantity ?? defaults.rooms);
    const roomsDivisor = rooms && rooms > 0 ? rooms : 1;
    // pricePerNight from the engine is already per single room; the derived fallback comes from the
    // all-rooms total, so it must be divided by rooms to match (price = one room per night).
    const derivedNightPrice = totalPrice !== null && nights ? totalPrice / nights / roomsDivisor : null;
    const explicitNightPrice = toNumber(
        source.price_per_night ??
        source.pricePerNight ??
        defaults.price_per_night ??
        defaults.pricePerNight
    );
    const fallbackPrice = toNumber(source.price ?? defaults.price);
    const price = roundMoney(explicitNightPrice ?? derivedNightPrice ?? fallbackPrice);
    const quantity = rooms;
    const itemId = pickString(source.item_id, source.hotel_id, source.id, defaults.item_id);
    const roomName = pickString(source.room_name, source.original_room_name, source.room, defaults.room_name);
    const hotelName = pickString(
        source.item_name,
        source.hotel_name,
        source.hotel?.name,
        source.hotel?.title,
        source.details?.hotel_name,
        source.details?.hotelName,
        defaults.item_name
    );
    const itemName = hotelName || (itemId && !source.place_id ? pickString(source.name, source.title) : null);

    return removeEmpty({
        item_id: itemId,
        item_name: itemName,
        item_category: getItemCategory(source, defaults) || 'Hotel',
        item_category2: getItemSubcategory(source, defaults),
        hotel_stars: toNumber(source.hotel_stars ?? source.stars ?? defaults.hotel_stars),
        property_type: getPropertyType(source, defaults),
        item_variant: includePurchaseVariant ? getPurchaseVariant(source, defaults, roomName) || 'Hotel' : null,
        room_name: roomName,
        price,
        index,
        quantity
    });
}

function buildListItems(properties) {
    if (Array.isArray(properties.hotels) && properties.hotels.length > 0) {
        return properties.hotels.slice(0, MAX_LIST_ITEMS)
            .map((hotel, index) => normalizeHotelItem(hotel, index + 1, { rooms: properties.rooms }))
            .filter((item) => item.item_id || item.item_name);
    }
    const fallbackIndex = (toNumber(properties.recommended_index) ?? 0) + 1;
    const item = normalizeHotelItem(properties, fallbackIndex || 1);
    return (item.item_id || item.item_name) ? [item] : [];
}

function hasItemPrice(hotel) {
    return [hotel.price_per_night, hotel.pricePerNight, hotel.total_price, hotel.totalPrice, hotel.price]
        .some((value) => toNumber(value) !== null);
}

// One priced item without a known currency, or two currencies, would mislabel the list, so it is omitted.
function getSharedListCurrency(properties) {
    const hotels = Array.isArray(properties.hotels) ? properties.hotels.slice(0, MAX_LIST_ITEMS) : [];
    const pricedCurrencies = hotels
        .filter((hotel) => hotel && hasItemPrice(hotel))
        .map((hotel) => pickString(hotel.currency, hotel.total_price_currency));
    const currencies = new Set(pricedCurrencies);
    return currencies.size === SINGLE_CURRENCY_COUNT && !currencies.has(null) ? pricedCurrencies[0] : null;
}

function buildFunnelEvent(eventName, properties, ecommerce = {}, extraFields = {}) {
    return removeEmpty({
        event: FUNNEL_EVENT,
        event_name: eventName,
        ...getCommonFields(properties),
        ...extraFields,
        ecommerce: removeEmpty(ecommerce)
    });
}

function transformViewItemList(properties) {
    const items = buildListItems(properties);

    return buildFunnelEvent('view_item_list', properties, {
        currency: getSharedListCurrency(properties),
        items
    });
}

function transformSearch(properties) {
    return removeEmpty({
        event: FUNNEL_EVENT,
        event_name: 'search',
        ...getCommonFields(properties)
    });
}

function transformSearchNoResults(properties) {
    return removeEmpty({
        event: FUNNEL_EVENT,
        event_name: 'search_no_results',
        ...getCommonFields(properties),
        results_count: 0
    });
}

function transformSelectItem(properties) {
    const selectedIndex = (toNumber(properties.recommended_index) ?? 0) + 1;
    const resultsCount = toNumber(
        properties.recommended_count ??
        properties.results_count ??
        properties.count
    );

    const preferredValue = resolvePreferredCurrency(getEventTotalPrice(properties), properties);
    const items = [normalizeHotelItem(properties, selectedIndex || 1, {}, { includePurchaseVariant: true })];

    return buildFunnelEvent(
        'select_item',
        properties,
        {
            currency: preferredValue.currency,
            items
        },
        {
            selected_rank: selectedIndex,
            results_count: resultsCount
        }
    );
}

function transformViewItem(properties) {
    const preferredValue = resolvePreferredCurrency(getEventTotalPrice(properties), properties);
    const items = [normalizeHotelItem(properties, 1, {}, { includePurchaseVariant: true })];

    return buildFunnelEvent('view_item', properties, {
        currency: preferredValue.currency,
        value: preferredValue.amount,
        items
    });
}

function transformAddToCart(properties) {
    const preferredValue = resolvePreferredCurrency(getEventTotalPrice(properties), properties);
    const items = [normalizeHotelItem(properties, 1, { quantity: properties.rooms }, { includePurchaseVariant: true })];

    return buildFunnelEvent('add_to_cart', properties, {
        currency: preferredValue.currency,
        value: preferredValue.amount,
        items
    });
}

function transformBeginCheckout(properties) {
    const checkoutValue = getEventTotalPrice(properties);
    const preferredValue = resolvePreferredCurrency(checkoutValue, properties);
    const items = [normalizeHotelItem(properties, 1, { quantity: properties.rooms }, { includePurchaseVariant: true })];

    return buildFunnelEvent('begin_checkout', properties, {
        currency: preferredValue.currency,
        value: preferredValue.amount,
        coupon: pickString(properties.coupon_code, properties.coupon),
        items
    });
}

function transformAddPaymentInfo(properties) {
    const preferredValue = resolvePreferredCurrency(getEventTotalPrice(properties), properties);
    const items = [normalizeHotelItem(properties, 1, { quantity: properties.rooms }, { includePurchaseVariant: true })];
    const pointsUsed = toNumber(
        properties.points_spent ??
        properties.user_chosen_points ??
        properties.points_used
    );
    const couponDescription = pickString(
        properties.coupon_description,
        properties.coupon_desc,
        properties.coupon_title,
        properties.coupon_name,
        properties.promotion_description
    );

    return buildFunnelEvent('add_payment_info', properties, {
        currency: preferredValue.currency,
        value: preferredValue.amount,
        payment_type: pickString(properties.payment_method, properties.payment_type, 'credit_card'),
        coupon: couponDescription,
        items
    }, {
        points_used: pointsUsed,
        coupon_description: couponDescription
    });
}

function transformPaymentFailed(properties) {
    const preferredValue = resolvePreferredCurrency(getEventTotalPrice(properties), properties);
    const items = [normalizeHotelItem(properties, 1, { quantity: properties.rooms }, { includePurchaseVariant: true })];

    return buildFunnelEvent(
        'payment_failed',
        properties,
        {
            currency: preferredValue.currency,
            value: preferredValue.amount,
            payment_type: pickString(properties.payment_method, properties.payment_type),
            items
        },
        {
            error_code: normalizeErrorCode(properties.error, properties.failure_reason)
        }
    );
}

function transformPurchase(properties) {
    const pointsSpent = toNumber(
        properties.points_spent ??
        properties.user_chosen_points ??
        properties.points_used
    ) ?? 0;
    const pointsCashValue = toNumber(
        properties.transaction_cash_value_of_points ??
        properties.points_in_currency
    ) ?? (pointsSpent * (toNumber(properties.points_ex_rate) ?? 0));
    const totalValue = toNumber(
        properties.total_price ??
        properties.totalPrice ??
        properties.total_value ??
        properties.value
    ) ?? 0;
    const preferredValue = resolvePreferredCurrency(totalValue, properties);
    const tax = roundMoney(properties.tax);
    const items = [normalizeHotelItem(properties, 1, { quantity: properties.rooms }, { includePurchaseVariant: true })];

    return {
        ...buildFunnelEvent(
            'purchase',
            properties,
            {
                transaction_id: pickString(properties.booking_id, properties.short_id, properties.reservation_id),
                value: preferredValue.amount,
                currency: preferredValue.currency,
                payment_type: pickString(properties.payment_method, properties.payment_type),
                tax,
                coupon: pickString(properties.coupon_code, properties.coupon),
                items
            },
            {
                payment_type: pickString(properties.payment_method, properties.payment_type),
                points_spent: pointsSpent,
                transaction_cash_value_of_points: roundMoney(pointsCashValue),
                total_value: roundMoney(totalValue),
                free_cancellation_until: formatDate(properties.cancellation_date)
            }
        )
    };
}

function transformRefund(properties) {
    const totalValue = toNumber(
        properties.total_price ??
        properties.totalPrice ??
        properties.total_value ??
        properties.value
    );
    const pointsCashValue = toNumber(
        properties.transaction_cash_value_of_points ??
        properties.points_in_currency
    ) ?? 0;
    const refundedValue = Math.max((totalValue ?? 0) - pointsCashValue, 0);
    const preferredValue = resolvePreferredCurrency(refundedValue, properties);
    const items = [normalizeHotelItem(properties, 1, { quantity: properties.rooms }, { includePurchaseVariant: true })];
    return buildFunnelEvent(
        'refund',
        properties,
        {
            transaction_id: pickString(properties.booking_id, properties.short_id, properties.reservation_id),
            value: preferredValue.amount,
            currency: preferredValue.currency,
            payment_type: pickString(properties.payment_type, properties.payment_method),
            items
        },
        {
            total_value: roundMoney(totalValue),
            order_date: formatDate(properties.order_date)
        }
    );
}

export function mapEventToGA4Ecommerce(event_name, properties) {
    const eventMapping = {
        EXECUTED_SEARCH: () => transformSearch(properties),
        SEARCH_WITHOUT_RESULTS: () => transformSearchNoResults(properties),
        SEARCH_WITHOUT_RESULTS_HOTEL: () => transformSearchNoResults(properties),
        RESULTS_RECEIVED_HOTELS: () => transformViewItemList(properties),
        SELECTED_HOTEL: () => transformSelectItem(properties),
        VIEWED_HOTEL_PAGE: () => transformViewItem(properties),
        SELECTED_DEAL: () => transformAddToCart(properties),
        PROCEEDED_TO_PAYMENT_OPEN_PAYMENT_FORM: () => transformBeginCheckout(properties),
        CREDIT_CARD_PAYMENT: () => transformAddPaymentInfo(properties),
        RESERVATION_FAILED_TO_BOOK: () => transformPaymentFailed(properties),
        BOOKING_CREATED: () => transformPurchase(properties),
        RESERVATION_CANCELED: () => transformRefund(properties)
    };

    const transformer = eventMapping[event_name];
    return transformer ? transformer() : null;
}

export {
    toNumber,
    roundMoney,
    pickString,
    normalizeDate,
    getPropertyType,
    hashMatmidId,
    getAdultsCount,
    getChildrenCount,
    getGuestsCount,
    getTripLength,
    getDaysToTrip
};

export function toBrandNeutralFunnelEvent(funnelEvent) {
    if (!funnelEvent) {
        return null;
    }

    return Object.fromEntries(
        Object.entries(funnelEvent).filter(([field]) => BRAND_NEUTRAL_FUNNEL_FIELDS.has(field))
    );
}

export { BRAND_NEUTRAL_FUNNEL_FIELDS };
